import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import KeyboardArrowLeft from "../../components/icon-components/KeyboardArrowLeft";
import MilestoneContentForm from "../../components/milestone-content-form/MilestoneContentForm";
import {
  PAGE_HEADER_CREATE_MILESTONE_CONTENT,
  TAB_LABEL_EDUCATION_CONTENT,
  TAB_LABEL_MILESTONE_DETAILS,
} from "../../utility/constants/locale";
import CustomTab from "../../components/custom-tab/CustomTab";
import {
  CATEGORY_ID_MAPPING,
  CREATE_MILESTONE_TABS,
  MILESTONE_MESSAGES,
  PATIENTTASK_STATUS_ID_MAPPING,
} from "../../utility/constants/common-constants";
import "./CreateMilestone.scss";
import EducationContentForm from "../../components/education-content-form/EducationContentForm";
import { useCreateMilestoneTaskMutation } from "./services/CreateMilestoneAPISlice";
import { useSnackbar } from "notistack";
import { useAppSelector } from "../../store/hooks";
import { getStatusIDFromStatusName } from "../../utility/functions/common-functions";
import CustomSpinner from "../../components/custom-spinner/CustomSpinner";

const CreateMilestone: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [activeTab, setActiveTab] = useState(TAB_LABEL_MILESTONE_DETAILS);
  const [createMilestoneTask] = useCreateMilestoneTaskMutation();
  const [loading, setLoading] = useState(false);
  const displayGroups = useAppSelector(
    (state) => state.adminMasterData.displayGroups
  );
  const user = useAppSelector((state) => state.adminAuth.user!);
  const categoryWiseStatus = useAppSelector(
    (state) => state.adminMasterData.categoryWiseStatus
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  const handleFormSubmit = async (values: any) => {
    setLoading(true);
    const statusId = getStatusIDFromStatusName(
      CATEGORY_ID_MAPPING.patientTask,
      PATIENTTASK_STATUS_ID_MAPPING.active,
      categoryWiseStatus
    );
    try {
      const body = {
        ...values,
        statusId,
        createdBy: user.name || "system",
        isEducationMilestone: activeTab === TAB_LABEL_EDUCATION_CONTENT,
        milestoneLearningUrls: window.btoa(values.milestoneLearningUrls)
      };

      await createMilestoneTask({
        body,
        accessToken: user.accessToken,
      }).unwrap();
      enqueueSnackbar(
        activeTab === TAB_LABEL_MILESTONE_DETAILS
          ? MILESTONE_MESSAGES.CreateMilestone.carePlan.Success
          : MILESTONE_MESSAGES.CreateMilestone.education.Success,
        {
          variant: "success",
        }
      );
      navigate("/admin");
    } catch (error) {
      enqueueSnackbar(
        activeTab === TAB_LABEL_MILESTONE_DETAILS
          ? MILESTONE_MESSAGES.CreateMilestone.carePlan.Fail
          : MILESTONE_MESSAGES.CreateMilestone.education.Fail,
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async (values: any) => {
    setLoading(true);
    const statusId = getStatusIDFromStatusName(
      CATEGORY_ID_MAPPING.patientTask,
      PATIENTTASK_STATUS_ID_MAPPING.draft,
      categoryWiseStatus
    );
    try {
      const body = {
        ...values,
        statusId,
        createdBy: user.name || "system",
        isEducationMilestone: activeTab === TAB_LABEL_EDUCATION_CONTENT,
        milestoneLearningUrls: window.btoa(values.milestoneLearningUrls)
      };

      await createMilestoneTask({
        body,
        accessToken: user.accessToken,
      }).unwrap();
      enqueueSnackbar(
        activeTab === TAB_LABEL_MILESTONE_DETAILS
          ? MILESTONE_MESSAGES.SaveDraft.carePlan.Success
          : MILESTONE_MESSAGES.SaveDraft.education.Success,
        {
          variant: "success",
        }
      );
      navigate("/admin");
    } catch (error) {
      enqueueSnackbar(
        activeTab === TAB_LABEL_MILESTONE_DETAILS
          ? MILESTONE_MESSAGES.SaveDraft.carePlan.Fail
          : MILESTONE_MESSAGES.SaveDraft.education.Fail,
        {
          variant: "error",
        }
      );
    } finally {
      setLoading(false);
    }
  };

  const [initialValues, setInitialValues] = useState({
    isMulticode: false,
    sourceMilestoneCode: "",
    milestoneTypeId: 0,
    milestoneDisplayName: "",
    rewardPoints: 0,
    displayGroupId: 0,
    statusId: 0,
    educationalContentTypeId: 0,
    milestoneLearningUrls: "",
    description: "",
  });

  return (
    <Box
      p={3}
      className="createMilestoneContainer"
    >
      <Box
        display="flex"
        alignItems="center"
        mb={2}
      >
        <IconButton
          onClick={() => navigate("/admin")}
          aria-label="back button"
        >
          <KeyboardArrowLeft />
        </IconButton>
        <Typography variant="h2">
          {PAGE_HEADER_CREATE_MILESTONE_CONTENT}
        </Typography>
      </Box>
      <Box
        display="flex"
        alignItems="center"
        borderBottom="1px solid #BCA685"
        justifyContent="space-between"
        mb={2}
      >
        <CustomTab
          value={activeTab}
          handleTabChange={handleTabChange}
          tabs={CREATE_MILESTONE_TABS}
        />
      </Box>
      {activeTab === TAB_LABEL_MILESTONE_DETAILS && (
        <MilestoneContentForm
          initialValues={initialValues}
          onSubmit={handleFormSubmit}
          onSaveDraft={handleSaveDraft}
          isEditMode={false}
          milestoneData={displayGroups}
        />
      )}
      {activeTab === TAB_LABEL_EDUCATION_CONTENT && (
        <Box mt={3}>
          <EducationContentForm
            initialValues={initialValues}
            onSubmit={handleFormSubmit}
            onSaveDraft={handleSaveDraft}
            isEditMode={false}
            milestoneData={displayGroups}
          />
        </Box>
      )}
      {loading && <CustomSpinner />}
    </Box>
  );
};

export default CreateMilestone;
